import { Obj } from "scrivito"
import { BuildingReferenceObj } from "scrivito/Objs/BuildingReference/BuildingReferenceObjClass"
import {
  ProductAttributes,
  ProductObj
} from "scrivito/Objs/Product/ProductObjClass"
import {
  ProductBaseVariantAttributes,
  ProductBaseVariantObj
} from "scrivito/Objs/ProductBaseVariant/ProductBaseVariantObjClass"
import { Project } from "shared/types/Project"
import { BuildingType } from "shared/types/references/BuildingType"

import { TeamRole } from "entities/team-members/types/TeamRole"

import { SelectOption } from "../../../shared/types/Select"

export enum GlobalSearchResultType {
  PROJECT = "PROJECT",
  PRODUCT = "PRODUCT",
  BUILDING_REFERENCE = "BUILDING_REFERENCE"
}

export interface ProjectMatchInfo {
  title?: FieldMatch
}

export interface ProjectSearchResult {
  project: Project
  matchInfo: ProjectMatchInfo
}

export interface FieldMatch {
  value?: string
  searchTerm?: string
  ranges?: number[]
}

export interface ProjectOption
  extends SelectOption<string, { project: Project; role: TeamRole }> {
  id: string
  value: string
  type: GlobalSearchResultType.PROJECT
  data: { project: Project; role: TeamRole; matchInfo: ProjectMatchInfo }
}

export interface ProjectOptionsGroup {
  type: GlobalSearchResultType.PROJECT
  label?: string
  options: ProjectOption[]
}

export interface ProductOption
  extends SelectOption<
    string,
    {
      obj: Obj<ProductAttributes | ProductBaseVariantAttributes>
      variantKey?: string
      articleNumber?: string
      matchInfo: ProductMatchInfo
    }
  > {
  id: string
  type: GlobalSearchResultType.PRODUCT
  data: {
    obj: Obj<ProductAttributes | ProductBaseVariantAttributes>
    variantKey?: string
    articleNumber?: string
    matchInfo: ProductMatchInfo
  }
}

export interface ProductOptionsGroup {
  type: GlobalSearchResultType.PRODUCT
  label?: string
  options: ProductOption[]
}

export interface BuildingReferenceSearchResult {
  id: string
  title: string
  obj: BuildingReferenceObj
  matchInfo: BuildingReferenceMatchInfo
}

export interface BuildingReferenceMatchInfo {
  title?: FieldMatch
  products?: FieldMatch[]
  buildingTypes?: Partial<Record<BuildingType, FieldMatch>>
}

export interface BuildingReferenceOption
  extends SelectOption<
    string,
    {
      obj: BuildingReferenceObj
    }
  > {
  id: string
  type: GlobalSearchResultType.BUILDING_REFERENCE
  data: {
    obj: BuildingReferenceObj
    matchInfo: BuildingReferenceMatchInfo
  }
}

export interface BuildingReferenceOptionsGroup {
  type: GlobalSearchResultType.BUILDING_REFERENCE
  label?: string
  options: BuildingReferenceOption[]
}

export type GlobalSearchOption =
  | ProjectOption
  | ProductOption
  | BuildingReferenceOption

export type GlobalSearchOptionsGroup =
  | BuildingReferenceOptionsGroup
  | ProjectOptionsGroup
  | ProductOptionsGroup

export type GlobalSearchOptionsMap = {
  [K in GlobalSearchResultType]?: Extract<
    GlobalSearchOptionsGroup,
    { type: K }
  > | null
}

export interface GlobalSearchModel {
  options: GlobalSearchOptionsMap
  isLoading: boolean
  searchTerm: string | null
  onSearchTermChange: (value: string | null) => void
  history: string[]
}

export interface ProductMatchInfo {
  title?: FieldMatch
  variantKey?: FieldMatch
  articleNumber?: FieldMatch
}

export interface ProductSearchResult {
  id: string
  title: string
  productType: string
  articleNumber?: string
  variantKey?: string
  obj: ProductObj | ProductBaseVariantObj
  matchInfo: ProductMatchInfo
}

export const GLOBAL_SEARCH_ALL_TAB = "ALL" as const
export type GlobalSearchActiveTab =
  | typeof GLOBAL_SEARCH_ALL_TAB
  | GlobalSearchResultType

export const GLOBAL_SEARCH_HISTORY_OPTION_TYPE = "HISTORY" as const

export interface GlobalSearchHistoryOption {
  type: typeof GLOBAL_SEARCH_HISTORY_OPTION_TYPE
  value: string
  label: string
}

export type GlobalSearchSelectableOption =
  | GlobalSearchOption
  | GlobalSearchHistoryOption
